import React, { useCallback, useContext, useState } from 'react';
import BaseModal from 'components/modals/BaseModal';
import PhotoUploader from 'components/fileUploads/PhotoUploader';
import Button from 'components/buttons/Button';
import axios from 'data/axios';
import { setUser } from 'contexts/AppActions';
import mapUserProfile from 'models/mapping/mapUserProfile';
import AppContext from 'contexts/AppContext';
import toast from 'react-hot-toast';
import LoadingSpinner from 'components/LoadingSpinner';
import IconButton from 'components/buttons/IconButton';
import { X } from 'kzv-icons';
import { useQuery } from 'react-query';
import { fetchDefaultAvatars } from 'data/fetchers';

interface UpdateAvatarModalProps {
    open: boolean;
    handleClose: () => void;
}

const UpdateAvatarModal = ({ open, handleClose }: UpdateAvatarModalProps) => {
    const [posting, setPosting] = useState(false);
    const [files, setFiles] = useState([]);
    const [selectedAvatar, setSelectedAvatar] = useState<string>();
    const { dispatch } = useContext(AppContext);
    const { data: defaultAvatars } = useQuery('defaultAvatars', () => fetchDefaultAvatars());

    const filesChanged = useCallback((files) => {
        setFiles(files);
        setSelectedAvatar(null);
    }, []);

    const avatarSelected = useCallback(
        (avatar) => {
            setSelectedAvatar(selectedAvatar === avatar.url ? null : avatar.url);
        },
        [selectedAvatar]
    );

    const handleSubmit = async () => {
        if (!selectedAvatar && files.length === 0) {
            return;
        }

        setPosting(true);

        try {
            let response;
            if (selectedAvatar) {
                response = await axios.post('/avatar', { url: selectedAvatar });
            } else {
                const formData = new FormData();
                formData.append('image', files[0]);
                response = await axios.post('/avatar', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
            }
            const updatedUser = response.data.data;
            setUser(dispatch, mapUserProfile(updatedUser));
            toast.success('Avatar uspešno posodobljen');
            handleClose();
            setPosting(false);
        } catch (error) {
            console.log(error);
            toast.error(
                'Pri posodabljanju prikazne fotografije je šlo nekaj narobe. Največja dovoljena velikost fotografije je 10mb.'
            );
            setPosting(false);
        }
    };

    return (
        <BaseModal open={open} handleClose={handleClose}>
            <div className="max-w-lg relative p-6">
                <div className="sm:hidden absolute top-0 right-0 text-2xl">
                    <IconButton color="secondary" onClick={handleClose}>
                        <X strokeWidth={2} />
                    </IconButton>
                </div>
                <h3 className="text-center">Zamenjaj prikazno fotografijo</h3>
                <div className="mt-2 p-4 text-center">
                    <PhotoUploader handleFilesChange={filesChanged} maxPhotos={1} />
                </div>
                <div className="divider flex gap-6 mt-8 mb-6 items-center">
                    <div className="w-full h-[1px] bg-gray-300"></div>
                    <div className="font-serif flex-shrink-0 text-lg">Ali pa si izberi avatarčka</div>
                    <div className="w-full h-[1px] bg-gray-300"></div>
                </div>
                <div className="grid grid-cols-4 items-center place-center gap-2 place-content-center">
                    {defaultAvatars?.data?.map((avatar, index) => (
                        <button
                            key={index}
                            className={`justify-self-center rounded-full h-20 w-20 flex justify-center items-center cursor-pointer ${
                                selectedAvatar === avatar.url
                                    ? 'border-2 border-primary bg-brand hover:bg-brand'
                                    : 'hover:bg-brand-dull'
                            }`}
                            onClick={() => avatarSelected(avatar)}
                        >
                            <img src={avatar.url} alt="" className="w-16 h-16" />
                        </button>
                    ))}
                </div>
                <div className="flex mt-8 justify-between items-center">
                    <Button
                        variant="text"
                        color="secondary"
                        onClick={(e) => {
                            e.preventDefault();
                            handleClose();
                        }}
                    >
                        Zapri
                    </Button>
                    <Button
                        color="secondary"
                        disabled={(files.length === 0 && !selectedAvatar) || posting}
                        onClick={handleSubmit}
                    >
                        {posting ? 'Shranjujem' : 'Shrani'}
                        {posting && (
                            <span className="ml-4">
                                <LoadingSpinner width={'1.5em'} height={'1.5em'} />
                            </span>
                        )}
                    </Button>
                </div>
            </div>
        </BaseModal>
    );
};

export default UpdateAvatarModal;
